export type UserRole = "admin" | "employee" | "beneficiary" | "investor" | "editor" | "viewer";
export type UserStatus = "active" | "inactive" | "pending";

// User profile DTOs (moved from infrastructure/api/userProfileService)
export interface UpdateUserProfileRequest {
  first_name?: string;
  last_name?: string;
  profile_picture?: string | null;
}

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
}

export interface UserPreferences {
  prefers_dark_mode: boolean;
  prefers_compact_view: boolean;
  language: string;
  timezone: string;
  notifications_enabled: boolean;
  notify_via_email: boolean;
  notify_via_app: boolean;
}

export interface UpdateUserPreferencesRequest {
  prefers_dark_mode?: boolean;
  prefers_compact_view?: boolean;
  language?: string;
  timezone?: string;
  notifications_enabled?: boolean;
  notify_via_email?: boolean;
  notify_via_app?: boolean;
}

export interface UserProfileResponse {
  uuid: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  full_name: string;
  is_verified: boolean;
  last_login_at?: string | null;
  profile_picture_signed_url?: string | null;
  preferences?: UserPreferences | null;
  pending_email?: string | null;
  pending_email_requested_at?: string | null;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  joinDate?: Date;
  phone?: string;
  avatar?: string;
  profile_picture_signed_url?: string; // Signed URL from backend for profile picture

  // Additional fields from backend
  first_name?: string | null;
  last_name?: string | null;
  is_verified?: boolean;
  last_login_at?: string | null;
  uuid?: string;
}

// Domain class with business logic
export class UserEntity implements User {
  public readonly id: string;
  public readonly name: string;
  public readonly email: string;
  public readonly role: UserRole;
  public readonly status: UserStatus;
  public readonly joinDate?: Date;
  public readonly phone?: string;
  public readonly avatar?: string;
  public readonly profile_picture_signed_url?: string;

  constructor(user: User) {
    this.id = user.id;
    this.name = user.name;
    this.email = user.email;
    this.role = user.role;
    this.status = user.status;
    this.joinDate = user.joinDate;
    this.phone = user.phone;
    this.avatar = user.avatar;
    this.profile_picture_signed_url = user.profile_picture_signed_url;
  }

  // Business logic methods
  isActive(): boolean {
    return this.status === "active";
  }

  canLogin(): boolean {
    return this.isActive();
  }

  hasAnyRole(roles: UserRole[]): boolean {
    return roles.includes(this.role);
  }

  isInvestor(): boolean {
    return this.role === "investor";
  }

  // Validation methods
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  static validateUser(userData: Partial<User>): string[] {
    const errors: string[] = [];

    if (!userData.name || userData.name.trim().length === 0) {
      errors.push("Name is required");
    }

    if (!userData.email || !UserEntity.isValidEmail(userData.email)) {
      errors.push("Valid email is required");
    }

    // Allow more flexible role validation - accept any string and default to viewer if needed
    if (!userData.role) {
      // Don't throw error, just assign default role
      (userData as User).role = "viewer";
    } else if (
      !["admin", "employee", "beneficiary", "investor", "editor", "viewer"].includes(userData.role)
    ) {
      // If role is not recognized, default to viewer
      (userData as User).role = "viewer";
    }

    // Allow more flexible status validation
    if (!userData.status) {
      // Don't throw error, just assign default status
      (userData as User).status = "active";
    } else if (!["active", "inactive", "pending"].includes(userData.status)) {
      // If status is not recognized, default to active
      (userData as User).status = "active";
    }

    return errors;
  }

  // Factory method
  static create(userData: User): UserEntity {
    const validationErrors = UserEntity.validateUser(userData);
    if (validationErrors.length > 0) {
      throw new Error(`Invalid user data: ${validationErrors.join(", ")}`);
    }
    return new UserEntity(userData);
  }
}

export function getUserDisplayName(
  user: Pick<User, "first_name" | "name" | "email"> | null | undefined,
  fallback: string,
): string {
  if (!user) return fallback;
  return user.first_name || user.name || user.email?.split("@")[0] || fallback;
}

export interface ProfileNameParts {
  firstName: string;
  lastName: string;
}

export function splitFullName(fullName: string | null | undefined): ProfileNameParts {
  const nameParts = fullName?.split(" ") || [];
  return {
    firstName: nameParts[0] || "",
    lastName: nameParts.slice(1).join(" ") || "",
  };
}

export function getProfileNameDefaults(
  user: Pick<User, "first_name" | "last_name" | "name">,
): ProfileNameParts {
  const fromFullName = splitFullName(user.name);
  return {
    firstName: user.first_name || fromFullName.firstName,
    lastName: user.last_name || fromFullName.lastName,
  };
}

export const DEFAULT_USER_TIMEZONE = "Europe/Madrid";

export interface TimezoneOption {
  value: string;
  label: string;
}

export const USER_TIMEZONE_OPTIONS: readonly TimezoneOption[] = [
  { value: "Europe/Madrid", label: "Madrid (CET/CEST)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Paris (CET/CEST)" },
  { value: "Europe/Berlin", label: "Berlin (CET/CEST)" },
  { value: "Europe/Rome", label: "Rome (CET/CEST)" },
  { value: "Europe/Amsterdam", label: "Amsterdam (CET/CEST)" },
  { value: "Europe/Zurich", label: "Zurich (CET/CEST)" },
  { value: "America/New_York", label: "New York (EST/EDT)" },
  { value: "America/Los_Angeles", label: "Los Angeles (PST/PDT)" },
  { value: "America/Chicago", label: "Chicago (CST/CDT)" },
  { value: "America/Denver", label: "Denver (MST/MDT)" },
  { value: "America/Toronto", label: "Toronto (EST/EDT)" },
  { value: "America/Mexico_City", label: "Mexico City (CST/CDT)" },
  { value: "America/Sao_Paulo", label: "São Paulo (BRT)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Asia/Shanghai", label: "Shanghai (CST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Australia/Sydney", label: "Sydney (AEDT/AEST)" },
  { value: "Pacific/Auckland", label: "Auckland (NZDT/NZST)" },
];

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  prefers_dark_mode: false,
  prefers_compact_view: false,
  language: "es",
  timezone: DEFAULT_USER_TIMEZONE,
  notifications_enabled: true,
  notify_via_email: true,
  notify_via_app: true,
};

export function areNotificationChannelsEnabled(
  preferences: Pick<UserPreferences, "notifications_enabled">,
): boolean {
  return preferences.notifications_enabled;
}

export interface UserProfileChanges {
  firstName?: string;
  lastName?: string;
  profilePicture?: string | null;
}

export interface EmailChangeRequest {
  new_email: string;
  current_password: string;
}
